import Component from 'vue-class-component';
import Vue from 'vue';
import {Prop, Ref} from 'vue-property-decorator';
import {
    BOARD_ANNOTATION_OPTIONS,
    BOARD_EVENTS,
    DEFAULT_COLOR,
    HOMEWORK_TEMPLATE_DEFAULT_NAME
} from '@/values/ed-whiteboard.values';
import {_HOMEWORKS_STORE, HOMEWORKS_EVENTS} from '@/homework/values/homeworks.values';
import KEYS from '@/values/keys';
import {io, Socket} from 'socket.io-client';
import PersistentBoardDataUtils
    from '@/components/common/ed-board/ed-board-tools/ed-board-persistence/persistent-board-data-utils';
import {
    CLOSE_BOARD_SOCKET_DISCONNECTING_EVENT,
    DISCONNECTION_ALERT_TYPES,
    NETWORK_SETTINGS,
    SHOW_SOCKET_DISCONNECTING_EVENT
} from '@/core/values/app.values';
import EdWhiteboardMenu
    from '@/core/components/ed-meeting-transparency/ed-whiteboard-menu/ed-whiteboard-menu.component';
import {_MEETINGS_STORE} from '@/meetings/values/meetings.values';
import BoardPage from '@/homework/models/board-page.model';
import {waitForElement} from '@/utils/dom-utils';
import {AuthProvider} from '@/auth/providers/auth.provider';


@Component({})
export default class TaskWhiteboardMixin extends Vue {
    @Prop({required: false}) readonly boardGroupHash: string;
    @Prop({required: false}) readonly readOnlyBoardGroupHash: string;
    @Prop({required: false}) readonly templatesPages: any[];
    @Prop({required: false}) readonly courseId: number;
    @Prop({required: false}) readonly pdfUrl: string;
    @Prop({required: false}) readonly documentHash: string;
    @Ref() readonly boardMenu: EdWhiteboardMenu;
    @Ref() readonly pageContainer: HTMLDivElement;
    @Ref() readonly loading: any;


    public socket: Socket = null;
    public boardPages: BoardPage[] = [];
    public addedBoardPages: BoardPage[] = [];
    public showDisable: boolean = false;
    public showPdf: boolean = false;
    public loadPageAttrs: string = ''
    public totalPages: number = 0;
    public loadedPagesCount: number = 0;
    public initializedBoards: number = 0;
    public totalHeight: number = 0;
    public clientHeightHalf: number = 0;
    public currentBoardPage: any = 1;
    public currentPageInfo: {boardId: number} = null;
    public preDocumentId : string = 'pdf-';
    public isPreviousDisconnected : boolean = false;
    public isPreviousOffline: boolean = false;
    public homeworkSent: boolean = false;
    public homeworkSentByMe: boolean = false;
    public prePDFViewerId : string = 'pdf-viewer' + Math.random().toString(36).substring(2, 10);

    private pdfViewerElement: HTMLElement;

    get offlineMode() : boolean {
        return this.$store.state.AppStore.offlineMode;
    }

    get user() : any {
        return this.$store.state.AuthStore.user;
    }

    get userName(): string {
        return this.$store.state.AuthStore.user.userId;
    }

    get rootContainerRefName(): string {
        return 'root-container-task-whiteboard';
    }

    get boardGroupName(): string {
        return this.boardGroupHash || this.readOnlyBoardGroupHash;
    }

    get currentBoardUserId(): number {
        return this.$store.state.MeetingsStore.currentBoardUser.id;
    }

    get isBuyerRole(): boolean {
        return this.$store.state.AuthStore.role === KEYS.ROLES.BUYER;
    }

    get pages() {
        let pages = [];
        if (this.templatesPages) {
            pages = this.templatesPages.map((t, i) => ({
                id          : (i + 1),
                text        : t.name,
                type        : 'url',
                borderBottom: true
            }));
        }
        pages.push({
            id          : (pages.length + 1),
            text        : this.$i18n.t('courses.objects.blankTemplate'),
            type        : HOMEWORK_TEMPLATE_DEFAULT_NAME,
            borderBottom: false
        });
        return pages;
    }

    get wboTaskSocketUrl(): string {
        return this.$store.state.AuthStore.wboTaskSocketUrl;
    }

    get isPresentationMode(): boolean {
        return this.$store.state.CourseStore.isPresentationStudentMode || this.$store.state.CourseStore.isPresentationTeacherMode;
    }

    get isBoardPreviousDisconnectionSocket(): boolean {
        return this.$store.state.AppStore.isBoardPreviousDisconnectionSocket;
    }

    get isPresentationModeGuestTeacher(): boolean {
        return this.$store.state.MeetingsStore.isPresentationModeGuestTeacher;
    }

    get isPresentationTeacherMode(): boolean {
        return this.$store.state.CourseStore.isPresentationTeacherMode;
    }

    get isSocketReady(): boolean {
        return this.socket && this.socket.connected;
    }

    /**
     * This is a handler for the event that is emitted by <ed-pdf-viewer> when a page is loaded
     */
    public loadingPage(pageData: any): void {
        this.$edEventBus.$emit(`${BOARD_EVENTS.LOADED_PDF_PAGE}${pageData.page}`, pageData);
    }

    public finishBoardInit(): void {
        this.initializedBoards++;
        if (this.totalPages !== this.initializedBoards) {
            return;
        }
        this.showPdf = true;
        this.adjustPdfViewerStyle();
    }

    private adjustPdfViewerStyle() {
        waitForElement('[id^="' + this.prePDFViewerId + this.documentHash + '"]')
            .then((element) => {
                this.pdfViewerElement = element;
                this.pdfViewerElement.classList.add('overflow-hidden');
            });
    }

    public showMessage(titleKey: string, bodyKey: string, type: string, action: Function): void {
        this.$EdModal.createMessageModal({
            title: this.$t(titleKey),
            body : this.$t(bodyKey),
            type,
            action
        });
    }

    public adjustPageLoaded(pageNumber: number) {
        this.defineClientHeightHalf();
        this.defineCurrentPage(this.currentBoardPage);
        let pdfOffsetWidth = this.pageContainer.offsetWidth;
        (this.$refs[this.rootContainerRefName] as HTMLElement).style.setProperty('width', `${pdfOffsetWidth}px`);
    }

    public defineClientHeightHalf() : void {
        let pdfClientHeight =  this.pageContainer.clientHeight;
        this.clientHeightHalf = pdfClientHeight / 2;
    }


    public showLoading(label?: string): void {
        try {
            if (this.loading) {
                if (label) {
                    const i18Text = this.$i18n.t(label);
                    this.loading['show'](i18Text);
                } else {
                    this.loading['show']();
                }
            }
        } catch (e) {
            //
        }
    }

    public hideLoading(): void {
        if (this.loading) {
            this.loading['hide']();
        }
    }

    public finishLoading(): void {
        this.hideLoading();
        this.boardMenu?.validateUndoOrRedoOption();
    }

    public setDefaultColor(): void {
        this.boardMenu?.setColor(this.isBuyerRole ? DEFAULT_COLOR.DRAFT : DEFAULT_COLOR.DRAFT_TEACHER);
    }


    public  connect(): any {
        // Destroy socket if one already exists
        this.disconnectSocket();

        this.socket = io(this.wboTaskSocketUrl, {
            ...NETWORK_SETTINGS.socketIo.connection,
            transports: ['websocket'],
            auth: {token: AuthProvider.getToken()},
        });

        this.socket.io.on('reconnect_attempt', () => {
            this.socket.auth = {token: AuthProvider.getToken()};
        });

        //Receive connect client socket event from the server
        this.socket.on('connect', () => {
            this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.SET_CLIENT_ID, this.socket.id);
            this.checkConnectionStatus(false);
        });

        //Receive disconnected socket event from the server
        this.socket.on('disconnect', () => {
            this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.SET_CLIENT_ID, '');
            this.$nextTick(() => {
                if (this.socket) {
                    this.checkConnectionStatus(true);
                }
            });
        });

        //Receive undoRedoPointer instructions from the server
        this.socket.on('undoRedoPointer', (pointerMsg: any) => {
            if (!pointerMsg.boardName) {
                return;
            }

            this.$edEventBus.$emit(`${pointerMsg.boardName}${BOARD_EVENTS.UNDO_REDO}`, pointerMsg);
        });

        this.socket.on('board_read_only', () => {
            this.showBoardNotAvailableMessage();
        });

        this.socket.on('board_joined', (msg: any) => {
            if (!msg.boardName) {
                return;
            }
            this.$edEventBus.$emit(`${msg.boardName}${BOARD_EVENTS.JOINED}`, msg);
        });

        this.socket.on('board_annotation_updated', ({
                                                        operationId,
                                                        fullName
                                                    }: { operationId: number, fullName: string }) => {
            PersistentBoardDataUtils.saveOperationId(this.userName, this.boardGroupHash, operationId);
            let bodyMessage = this.$t('homeworks.newHomeworkVersionUploaded');
            if (fullName) {
                bodyMessage = bodyMessage.toString().replace('{fullName}', fullName);
            } else {
                bodyMessage = bodyMessage.toString().replace('{fullName}', this.$t('homeworks.someone').toString());
            }
            this.showMessage('homeworks.homeworkUpdated', bodyMessage, KEYS.EVENT_TYPES.WARNING,
                () => {
                    this.loadedPagesCount = 0;
                    this.$edEventBus.$emit(BOARD_EVENTS.BOARD_ANNOTATIONS_UPDATED);
                });
        });

        this.socket.on('board_updated', (page: number) => {

        });

        //Receive draw instructions from the server
        this.socket.on('broadcast', async (msg: any) => {
            if (!msg.boardName || this.isPreviousOffline) {
                return;
            }

            this.$edEventBus.$emit(msg.boardName, msg);
        });

        // User disconnected from collaborative blank whiteboard
        this.socket.on('socketDisconnected', async (msg: any) => {
            this.$edEventBus.$emit(BOARD_EVENTS.USER_DISCONNECTED, msg);
        });

        //Receive all ids to remove from the server
        this.socket.on('removeAllByUser', (message: any) => {
            if (!message.boardName || this.isPreviousOffline) {
                return;
            }
            if (message.operationId) {
                PersistentBoardDataUtils.saveOperationId(this.userName, this.boardGroupHash, message.operationId);
            }
            this.$edEventBus.$emit(`${message.boardName}${BOARD_EVENTS.REMOVE_ELEMENTS}`, message.elementsToDelete);
        });
    }

    public showBoardNotAvailableMessage() {
        // Implemented by the classes using this mixin
    }


    public checkConnectionStatus(isDisconnected: boolean): void {
        this.isPreviousDisconnected = isDisconnected;
        if (isDisconnected) {
            this.showDisconnectedAlert();
            return;
        } else if (!isDisconnected && this.isBoardPreviousDisconnectionSocket) {
            let messageType: string = '';
            if (!this.isPresentationTeacherMode && !this.isPresentationModeGuestTeacher) {
                messageType = DISCONNECTION_ALERT_TYPES.toast;
            }
            this.$edEventBus.$emit(CLOSE_BOARD_SOCKET_DISCONNECTING_EVENT, messageType);
        }

    }

    public showDisconnectedAlert() {
        if (this.isPresentationMode) {
            this.$edEventBus.$emit(SHOW_SOCKET_DISCONNECTING_EVENT, {isBoard: true});
        } else {
            this.$edEventBus.$emit(SHOW_SOCKET_DISCONNECTING_EVENT, {
                isBoard: true,
                messageType: DISCONNECTION_ALERT_TYPES.toast
            });
        }
    }


    public disconnectSocket(): void {
        if (this.socket) {
            this.socket.disconnect();
            this.socket.removeAllListeners();
            this.socket = null;
        }
    }

    public registerDrawing(detail: any): void {
        if (!detail) {
            return;
        }
        //Deleted Annotations, save in store to Redo
        if (detail.type === 'delete') {
            this.boardMenu?.enableRedoOption();
            this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.UPDATE_BOARD_ANNOTATIONS, {
                ...detail,
                boardGroupHash  : this.boardGroupHash,
                boardAnnotations: BOARD_ANNOTATION_OPTIONS.redo
            });
            return;
        }

        //New Annotations, save in store to Undo
        this.boardMenu?.enableUndoOption();
        this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.UPDATE_BOARD_ANNOTATIONS, {
            ...detail,
            boardGroupHash  : this.boardGroupHash,
            boardAnnotations: BOARD_ANNOTATION_OPTIONS.undo
        });
    }


    public showCantOpenBoardError() {
        this.showMessage(
            'courses.info.infoTitle',
            'courses.errors.errorOpenWhiteboardObject',
            KEYS.EVENT_TYPES.DANGER,
            null
        );
    }

    public defineCurrentPage(boardId: number): void {
        this.currentBoardPage = boardId;
        this.currentPageInfo = {boardId}
    }

    mounted() {
        this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_INITIAL_BOARDS_LOADED_EMITED, false);
        this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_LOADED_BOARDS_IN_DOM, 0);
    }

    beforeDestroy() {
        this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_INITIAL_BOARDS_LOADED_EMITED, false);
        this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_LOADED_BOARDS_IN_DOM, 0);
    }

    get pdfViewer(): HTMLElement {
        if (!this.pdfViewerElement) {
            this.pdfViewerElement = document.querySelector(`[id^="${this.prePDFViewerId}${this.documentHash}"]`);
        }
        return this.pdfViewerElement;
    }
}
